<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $t('messages.absenteeism'),
                        href: null,
                    },
                    {
                        name: $t('messages.progress'),
                        href: null,
                    },
                    {
                        name: group.name,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="">
            <apexchart
                :type="chartData.chart.type"
                :height="chartData.chart.height"
                :series="chartData.series"
                :options="chartData"
            />
        </div>

        <form :action="meta.links.show" method="post" @submit.prevent="filter">
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.date')" for="date" />
                        <FormInput required id="date" v-model="form.date" name="date" type="date" class="w-full mt-1" />
                        <FormError :value="form.errors.date" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.min')" for="min" />
                        <FormInput
                            id="min"
                            v-model.number="form.min"
                            name="min"
                            type="number"
                            step="1"
                            class="w-full mt-1"
                        />
                        <FormError :value="form.errors.min" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.max')" for="max" />
                        <FormInput
                            id="max"
                            v-model.number="form.max"
                            name="max"
                            type="number"
                            step="1"
                            class="w-full mt-1"
                        />
                        <FormError :value="form.errors.max" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-3">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <div>
                                        {{ tableHeaders.dateStart }}
                                    </div>
                                    <div>
                                        {{ tableHeaders.dateMiddle }}
                                    </div>
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <div>
                                        {{ tableHeaders.dateMiddle }}
                                    </div>
                                    <div>
                                        {{ tableHeaders.dateEnd }}
                                    </div>
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.difference') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(student, index) in table"
                                :key="student.name"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5">
                                    {{ student.full_name }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.previous }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ student.total }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <span
                                        :class="{
                                            'text-green-600': student.difference < 0,
                                            'text-red-600': student.difference > 0,
                                        }"
                                    >
                                        {{ student.difference }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        PanelPage,
        FormLabel,
        FormError,
        FormInput,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'chartData', 'table', 'group', 'filters', 'tableHeaders'],
    data() {
        return {
            form: this.$inertia.form({
                date: this.filters.date,
                min: this.filters.min,
                max: this.filters.max,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.show);
        },
    },
};
</script>
